import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Maximize2, Minimize2, Minus, Plus, X } from "lucide-react";
import type { OpsArchiveMemberIdentity, OpsVisualProof } from "@business-ops/domain";
import { api } from "../api.js";
import { installDrawerFocusBoundary } from "./ui.js";
import "./ops-source-file-viewer.css";

type SourceImagePreview = { kind: "IMAGE"; dataUrl: string } | { kind: "OTHER" };
const RASTER_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);
const MAX_PREVIEW_BYTES = 12 * 1024 * 1024;

export async function loadOpsSourceFileAvailability(sourceEventId: string, signal: AbortSignal, visual?: OpsVisualProof, archiveMember?: OpsArchiveMemberIdentity): Promise<{ available: boolean; mimeType: string }> {
  signal.throwIfAborted();
  const response = await fetch(api.opsAssistantSourceFileUrl(sourceEventId, visual, archiveMember), {
    method: "HEAD", credentials: "same-origin", cache: "no-store", redirect: "error", signal
  });
  signal.throwIfAborted();
  return {
    available: response.ok,
    mimeType: response.ok ? response.headers.get("content-type")?.split(";", 1)[0]?.trim().toLowerCase() ?? "" : ""
  };
}

async function readPreviewImage(response: Response, mime: string, signal: AbortSignal): Promise<Blob> {
  // WHY [BOL-OPS-SOURCE-PREVIEW-BOUND-001]: response.blob() buffered the entire
  // original before enforcing any limit. A wrong/missing Content-Length must
  // not turn opening proof into an unbounded download. Count actual bytes and
  // cancel the owned stream on overflow/close; keep Open file for larger files.
  const declared = response.headers.get("content-length");
  const expected = declared === null ? null : Number(declared);
  if (expected !== null && (!Number.isSafeInteger(expected) || expected < 0 || expected > MAX_PREVIEW_BYTES)) {
    await response.body?.cancel();
    throw new Error("source_file_preview_too_large");
  }
  const reader = response.body?.getReader();
  if (!reader) throw new Error("source_file_empty");
  const parts: ArrayBuffer[] = [];
  let bytes = 0;
  let finished = false;
  const abort = () => { void reader.cancel().catch(() => {}); };
  signal.addEventListener("abort", abort, { once: true });
  try {
    signal.throwIfAborted();
    for (;;) {
      const next = await reader.read();
      signal.throwIfAborted();
      if (next.done) break;
      bytes += next.value.byteLength;
      if (bytes > MAX_PREVIEW_BYTES) throw new Error("source_file_preview_too_large");
      parts.push(Uint8Array.from(next.value).buffer);
    }
    if (!bytes) throw new Error("source_file_empty");
    if (expected !== null && expected !== bytes) throw new Error("source_file_incomplete");
    finished = true;
    return new Blob(parts, { type: mime });
  } finally {
    signal.removeEventListener("abort", abort);
    if (!finished) await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}

export async function loadOpsSourceImagePreview(sourceEventId: string, signal: AbortSignal, visual?: OpsVisualProof, archiveMember?: OpsArchiveMemberIdentity): Promise<SourceImagePreview> {
  signal.throwIfAborted();
  const response = await fetch(api.opsAssistantSourceFileUrl(sourceEventId, visual, archiveMember), {
    credentials: "same-origin", cache: "no-store", redirect: "error", signal
  });
  signal.throwIfAborted();
  if (!response.ok) throw new Error("source_file_unavailable");
  // FILE and its excerpt cannot identify a format. Only the authenticated
  // original-file owner's response MIME selects a raster preview. Documents
  // retain their native Open file action; HTML/SVG/video are never embedded.
  const mime = response.headers.get("content-type")?.split(";", 1)[0]?.trim().toLowerCase() ?? "";
  if (!RASTER_IMAGE_TYPES.has(mime)) {
    await response.body?.cancel();
    return { kind: "OTHER" };
  }
  const blob = await readPreviewImage(response, mime, signal);
  signal.throwIfAborted();
  if (!blob.size) throw new Error("source_file_empty");
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    const release = () => signal.removeEventListener("abort", abort);
    const abort = () => {
      reader.abort();
      release();
      reject(new DOMException("The preview was closed.", "AbortError"));
    };
    reader.onload = () => {
      release();
      if (typeof reader.result !== "string") reject(new Error("source_file_unavailable"));
      else resolve(reader.result);
    };
    reader.onerror = () => { release(); reject(new Error("source_file_unavailable")); };
    reader.onabort = () => { release(); reject(new DOMException("The preview was closed.", "AbortError")); };
    signal.addEventListener("abort", abort, { once: true });
    if (signal.aborted) abort();
    else reader.readAsDataURL(blob);
  });
  signal.throwIfAborted();
  return { kind: "IMAGE", dataUrl };
}

export function OpsSourceFileAction({ sourceEventId, maskPhones, discoverAttachment = false, visual, archiveMember }: {
  sourceEventId: string;
  maskPhones: boolean;
  discoverAttachment?: boolean;
  visual?: OpsVisualProof | undefined;
  archiveMember?: OpsArchiveMemberIdentity | undefined;
}) {
  // WHY [BOL-ARCHIVE-EXPLICIT-PROOF-LINK-001]: exact visual/member proof
  // already names a file. A legacy metadata-discovery timeout must not hide
  // its action; GET still rechecks the original/member identity and bytes.
  const shouldDiscover = discoverAttachment && !visual && !archiveMember;
  const fileIdentity = `${sourceEventId}:${visual?.originalSha256 ?? ""}:${archiveMember?.memberSha256 ?? ""}`;
  const [previewSource, setPreviewSource] = useState<string | null>(null);
  const [attachment, setAttachment] = useState<{ sourceEventId: string; available: boolean; mimeType: string } | null>(null);
  useEffect(() => { setPreviewSource(null); }, [fileIdentity, maskPhones]);
  useEffect(() => {
    if (!shouldDiscover) return;
    const controller = new AbortController();
    // Optional metadata must not keep a request alive indefinitely. This does
    // not impose a deadline on the answer, original-file reading or its model.
    const deadline = setTimeout(() => controller.abort(), 10_000);
    // WHY [BOL-OPS-HISTORIC-FILE-PROOF-001]: WhatsApp attachments retain TEXT
    // or TRANSCRIPT kind and old canonical answers have no file action. Probe
    // only the user-opened exact source via authenticated metadata HEAD; do not
    // relabel evidence, rewrite saved hashes, or download all claim attachments.
    // GET remains the byte/hash authority; a successful HEAD can become stale.
    void loadOpsSourceFileAvailability(sourceEventId, controller.signal, visual, archiveMember).then((value) => {
      if (!controller.signal.aborted) setAttachment({ sourceEventId, ...value });
    }).catch(() => {
      if (!controller.signal.aborted) setAttachment({ sourceEventId, available: false, mimeType: "" });
    }).finally(() => clearTimeout(deadline));
    return () => { clearTimeout(deadline); controller.abort(); };
  }, [sourceEventId, shouldDiscover, visual, archiveMember]);
  if (shouldDiscover && (attachment?.sourceEventId !== sourceEventId || !attachment.available)) return null;
  const canPreview = visual?.kind !== "PDF_PAGE" && (!shouldDiscover || RASTER_IMAGE_TYPES.has(attachment?.mimeType ?? ""));
  return <>
    <a className="ops-source-file-link" href={api.opsAssistantSourceFileUrl(sourceEventId, visual, archiveMember)} target="_blank" rel="noopener noreferrer">{visual?.kind === "PDF_PAGE" ? `Open page ${visual.pageNumber}` : "Open file"}</a>
    {/* WHY [BOL-OPS-SOURCE-IMAGE-VIEWER-001]: an explicit original-file action
        remains available, including in Private. Raster pixels cannot inherit
        phone masking, so the new preview is opt-in and unavailable in Private.
        Never preload pixels with proof, rewrite its text/hash, or infer MIME
        from a filename. Source/Private changes unmount and cancel the viewer. */}
    {!maskPhones && canPreview ? <button className="ops-source-file-link" type="button" onClick={() => setPreviewSource(fileIdentity)}>Preview</button> : null}
    {!maskPhones && previewSource === fileIdentity ? <OpsSourceImageViewer
      key={fileIdentity}
      sourceEventId={sourceEventId}
      visual={visual}
      archiveMember={archiveMember}
      onClose={() => setPreviewSource(null)}
    /> : null}
  </>;
}

function OpsSourceImageViewer({ sourceEventId, onClose, visual, archiveMember }: { sourceEventId: string; onClose: () => void; visual?: OpsVisualProof | undefined; archiveMember?: OpsArchiveMemberIdentity | undefined }) {
  const panelRef = useRef<HTMLElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [preview, setPreview] = useState<SourceImagePreview | null>(null);
  const [failed, setFailed] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [maximized, setMaximized] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    void loadOpsSourceImagePreview(sourceEventId, controller.signal, visual, archiveMember).then((value) => {
      if (!controller.signal.aborted) setPreview(value);
    }).catch(() => {
      if (!controller.signal.aborted) setFailed(true);
    });
    // The server's CSP already permits data: raster images. Keep this one
    // response in component memory only; there is no blob URL, browser storage,
    // new history state, or document/script embedding to retain or revoke.
    return () => controller.abort();
  }, [sourceEventId, visual, archiveMember]);
  useEffect(() => installDrawerFocusBoundary(panelRef.current, () => closeRef.current()), []);
  useEffect(() => {
    // The existing proof route owns browser Back and transcript restoration.
    // Leaving it must also close this transient child, never create a stale
    // preview entry or put file bytes into browser history.
    const leave = () => closeRef.current();
    window.addEventListener("popstate", leave);
    return () => window.removeEventListener("popstate", leave);
  }, []);
  const loading = !failed && (!preview || (preview.kind === "IMAGE" && !imageLoaded));
  return <div
    className={`ops-source-image-viewer${maximized ? " is-maximized" : ""}`}
    onPointerDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
    onKeyDown={(event) => {
      // Stop Escape here so the exact-source proof underneath stays open.
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
    }}
  >
    <section className="ops-source-image-viewer__panel" ref={panelRef} role="dialog" aria-modal="true" aria-label="Source file preview" tabIndex={-1}>
      <header className="ops-source-image-viewer__header">
        <button type="button" onClick={onClose}><ArrowLeft size={17} aria-hidden="true" />Back</button>
        <strong>Source file</strong>
        <button type="button" onClick={() => setMaximized((value) => !value)} aria-label={maximized ? "Restore preview size" : "Maximize preview"}>
          {maximized ? <Minimize2 size={18} aria-hidden="true" /> : <Maximize2 size={18} aria-hidden="true" />}
        </button>
        <button type="button" onClick={onClose} aria-label="Close file preview"><X size={18} aria-hidden="true" /></button>
      </header>
      <div className="ops-source-image-viewer__body" aria-busy={loading}>
        {loading ? <p role="status">Loading preview…</p> : null}
        {failed ? <p role="alert">The preview could not be loaded. Use Open file to view the original.</p> : null}
        {preview?.kind === "OTHER" ? <p>Use Open file to read this file in its supported viewer.</p> : null}
        {preview?.kind === "IMAGE" && !failed ? <div className={`ops-source-image-viewer__canvas ops-source-image-viewer__canvas--${zoom}`}>
          <img src={preview.dataUrl} alt="Original source image" onLoad={() => setImageLoaded(true)} onError={() => { setPreview(null); setFailed(true); }} />
        </div> : null}
      </div>
      <footer className="ops-source-image-viewer__footer">
        <a className="ops-source-file-link" href={api.opsAssistantSourceFileUrl(sourceEventId, visual, archiveMember)} target="_blank" rel="noopener noreferrer">Open file</a>
        {preview?.kind === "IMAGE" && !failed ? <div className="ops-source-image-viewer__zoom" aria-label="Image zoom">
          <button type="button" aria-label="Zoom out" disabled={zoom === 1} onClick={() => setZoom((value) => Math.max(1, value - 1))}><Minus size={17} aria-hidden="true" /></button>
          <button type="button" onClick={() => setZoom(1)}>Fit</button>
          <output aria-live="polite">{zoom}×</output>
          <button type="button" aria-label="Zoom in" disabled={zoom === 4} onClick={() => setZoom((value) => Math.min(4, value + 1))}><Plus size={17} aria-hidden="true" /></button>
        </div> : null}
      </footer>
    </section>
  </div>;
}
